<template>
  <div class="section">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/data-mapping">Data Mapping</router-link></li>
        <li class="is-active"><router-link to="/admin/validation-rules" aria-current="page">Validation Rules</router-link></li>
      </ul>
    </nav>
    <div class="container">
      <h1 class="title">Validation Rules</h1>

      <!-- Rules List -->
      <div class="box">
        <h2 class="subtitle">Existing Validation Rules</h2>
        <table class="table is-fullwidth">
          <thead>
            <tr>
              <th>Field</th>
              <th>Condition</th>
              <th>Value/Formula</th>
              <th>Error Message</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rule in rules" :key="rule.id">
              <td>{{ rule.field }}</td>
              <td>{{ rule.condition }}</td>
              <td>{{ rule.value }}</td>
              <td>{{ rule.error_message }}</td>
              <td>
                <button class="button is-info is-small" @click="openModal(rule)">Edit</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Add/Edit Rule Modal -->
      <div class="modal" :class="{ 'is-active': isModalActive }">
        <div class="modal-background" @click="closeModal"></div>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">{{ isEditing ? 'Edit Validation Rule' : 'Add Validation Rule' }}</p>
            <button class="delete" aria-label="close" @click="closeModal"></button>
          </header>
          <section class="modal-card-body">
            <form @submit.prevent="isEditing ? updateRule() : addRule()">
              <div class="field">
                <label class="label">Field</label>
                <div class="control">
                  <select v-model="newRule.field" class="input" :disabled="isEditing">
                    <option v-for="field in availableFields" :key="field.value" :value="field.value">
                      {{ field.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="field">
                <label class="label">Condition</label>
                <div class="control">
                  <select v-model="newRule.condition" class="input">
                    <option v-for="condition in conditions" :key="condition.value" :value="condition.value">
                      {{ condition.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="field">
                <label class="label">Value/Formula</label>
                <div class="control">
                  <input type="text" v-model="newRule.value" class="input" placeholder="Enter value or formula">
                </div>
              </div>
              <div class="field">
                <label class="label">Error Message</label>
                <div class="control">
                  <input type="text" v-model="newRule.error_message" class="input" placeholder="Enter error message">
                </div>
              </div>
            </form>
          </section>
          <footer class="modal-card-foot">
            <div class="buttons">
              <button class="button is-primary" @click="isEditing ? updateRule() : addRule()">
                {{ isEditing ? 'Save Changes' : 'Add Rule' }}
              </button>
              <button class="button" @click="closeModal">Cancel</button>
              <button
                v-if="isEditing"
                class="button is-danger"
                @click="confirmDeleteRule(newRule.id)"
              >
                Delete Rule
              </button>
            </div>
          </footer>
        </div>
      </div>

      <!-- Add Rule Button -->
      <div class="control mt-5">
        <button class="button is-primary" @click="openModal()">Add Validation Rule</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { toast } from "bulma-toast";

export default {
  name: "ValidationRules",
  data() {
    return {
      availableFields: [],
      conditions: [
        { value: "regex", label: "Regex Validation" },
        { value: ">", label: "Greater Than" },
        { value: "<", label: "Less Than" },
        { value: "=", label: "Equal To" },
        { value: "formula", label: "Formula Validation" },
      ],
      newRule: {
        id: null,
        field: "",
        condition: "",
        value: "",
        error_message: "",
      },
      isModalActive: false,
      isEditing: false,
      rules: [],
    };
  },
  methods: {
    async fetchFields() {
      try {
        const response = await axios.get("/api/v1/inputvalidation/invoice-fields/");
        this.availableFields = response.data;
      } catch (error) {
        console.error("Error fetching fields:", error);
        toast({
          message: "Failed to load fields.",
          type: "is-danger",
          duration: 3000,
          position: "top-right",
        });
      }
    },
    async fetchRules() {
      try {
        const response = await axios.get("/api/v1/inputvalidation/validation-rules/");
        this.rules = response.data.results || [];
      } catch (error) {
        console.error("Error fetching rules:", error);
        toast({
          message: "Failed to load validation rules.",
          type: "is-danger",
          duration: 3000,
          position: "top-right",
        });
      }
    },
    async addRule() {
      try {
        await axios.post("/api/v1/inputvalidation/validation-rules/", this.newRule);
        toast({
          message: "Validation rule added successfully.",
          type: "is-success",
          duration: 3000,
          position: "top-right",
        });
        this.closeModal();
        this.fetchRules();
      } catch (error) {
        console.error("Error adding rule:", error);
        toast({
          message: "Failed to add validation rule.",
          type: "is-danger",
          duration: 3000,
          position: "top-right",
        });
      }
    },
    async updateRule() {
      try {
        await axios.put(`/api/v1/inputvalidation/validation-rules/${this.newRule.id}/`, this.newRule);
        toast({
          message: "Validation rule updated successfully.",
          type: "is-success",
          duration: 3000,
          position: "top-right",
        });
        this.closeModal();
        this.fetchRules();
      } catch (error) {
        console.error("Error updating rule:", error);
        toast({
          message: "Failed to update validation rule.",
          type: "is-danger",
          duration: 3000,
          position: "top-right",
        });
      }
    },
    async confirmDeleteRule(id) {
      try {
        const confirmDelete = window.confirm("Are you sure you want to delete this rule?");
        if (!confirmDelete) return;

        await axios.delete(`/api/v1/inputvalidation/validation-rules/${id}/`);
        toast({
          message: "Validation rule deleted successfully.",
          type: "is-success",
          duration: 3000,
          position: "top-right",
        });
        this.closeModal();
        this.fetchRules();
      } catch (error) {
        console.error("Error deleting rule:", error);
        toast({
          message: "Failed to delete validation rule.",
          type: "is-danger",
          duration: 3000,
          position: "top-right",
        });
      }
    },
    openModal(rule = null) {
      this.isEditing = !!rule;
      this.newRule = rule
        ? { ...rule }
        : { id: null, field: "", condition: "", value: "", error_message: "" };
      this.isModalActive = true;
    },
    closeModal() {
      this.isModalActive = false;
    },
  },
  mounted() {
    this.fetchFields();
    this.fetchRules();
  },
};
</script>

<style scoped>
.table {
  margin-top: 20px;
}
.button.is-danger {
  margin-left: 10px;
}
.modal {
  z-index: 1000;
}
</style>
